import { useState } from 'react';
import { ChevronDown, ChevronRight, FileText } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { usePublicShareEncounters } from '../api/public-share-api';
import type { PublicEncounter } from '../types';
import { formatIsoDate } from '../utils/format';
import { PublicSection } from './PublicSection';

function statusVariant(status: string | null): 'default' | 'secondary' | 'outline' {
  if (status === 'complete') return 'default';
  if (status === 'submitted') return 'secondary';
  return 'outline';
}

function EncounterRow({ e }: { e: PublicEncounter }) {
  const [open, setOpen] = useState(false);
  const hasTranscript = !!e.transcript && e.transcript.trim().length > 0;
  const title = e.encounter_type || e.tag_name || 'Encounter';

  return (
    <li className="py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{title}</span>
            {e.tag_name && e.tag_name !== e.encounter_type && (
              <Badge variant="secondary" className="text-xs">{e.tag_name}</Badge>
            )}
            {e.status && (
              <Badge variant={statusVariant(e.status)} className="text-xs capitalize">
                {e.status}
              </Badge>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">
            {formatIsoDate(e.encounter_date) ?? '—'}
          </div>
        </div>
        {hasTranscript && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            Transcript
          </button>
        )}
      </div>
      {open && hasTranscript && (
        <div className="mt-2 rounded bg-muted/40 p-3 text-xs leading-relaxed whitespace-pre-wrap">
          {e.transcript}
        </div>
      )}
    </li>
  );
}

export function PublicEncountersList({ token }: { token: string }) {
  const { data, isLoading } = usePublicShareEncounters(token, true);
  const rows = data?.data ?? [];
  return (
    <PublicSection
      title="Encounters"
      icon={FileText}
      count={rows.length}
      isLoading={isLoading}
      emptyMessage="No encounters to show."
    >
      <ul className="divide-y">
        {rows.map((e) => (
          <EncounterRow key={e.id} e={e} />
        ))}
      </ul>
    </PublicSection>
  );
}
